import React, { useState } from "react";
import { useApi } from "../utils/Api";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const SpinnerOverlay = () => (
  <div className="spinner-overlay">
    <div className="spinner"></div>
  </div>
);

export default function ReservationsPage() {
  const { apiFetch } = useApi();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [seats, setSeats] = useState(1);
  const [specialRequest, setSpecialRequest] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });

  const today = new Date();
  const minDate = today.toLocaleDateString("en-CA");

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return; // 🔥 HARD BLOCK

    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    if (
      !firstName ||
      !lastName ||
      !phone ||
      !email ||
      !date ||
      !time ||
      !seats
    ) {
      setStatus({
        type: "error",
        message: "Please fill in all required fields.",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await apiFetch("book-table", {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        email: email.trim(),
        seats: parseInt(seats, 10),
        date,
        time,
        specialRequest: specialRequest?.trim() || "",
      });

      if (result?.success) {
        navigate("/thank-you");
      } else {
        setStatus({
          type: "error",
          message: result?.message || "Booking failed. Please try again.",
        });
      }
    } catch (err) {
      setStatus({
        type: "error",
        message: "Network/server error. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="relative min-h-screen flex flex-col items-center justify-center pt-24 pb-12 px-6 overflow-hidden">
      <Helmet>
        <title>Reserve a Table | Dee Delicious Afro-Caribbean Restaurant</title>

        <meta
          name="description"
          content="Book a table at Dee Delicious Afro-Caribbean Restaurant in Liverpool. Enjoy authentic Nigerian dishes and Jamaican flavours in a warm dining atmosphere."
        />

        <link rel="canonical" href="https://deedelicious.com/reservations" />

        <meta
          property="og:title"
          content="Reserve a Table | Dee Delicious"
        />
        <meta
          property="og:description"
          content="Reserve your table and enjoy authentic Afro-Caribbean dining in Liverpool."
        />
        <meta
          property="og:url"
          content="https://deedelicious.com/reservations"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://deedelicious.com/static/media/logo.06d3da75fe2458d68075.png"
        />

        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {isSubmitting && <SpinnerOverlay />}
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          alt="Restaurant Interior"
          className="w-full h-full object-cover opacity-20 dark:opacity-10 grayscale"
          src="https://lh3.googleusercontent.com/aida-public/AB6AXuB94lB3woVxhwSn5uCcZLIL3ElmQ0vHmstk83coasqx28RWx3BBRw0YnuCqIlUUCy4QEsPfgz8t5f59ndblbL8bYHviuNnNLCqLtcd7jox0bBf-xfuix6E5IaSS4m2QH3W-DStEgugK9ugmddFI0i4jw0qC29gZOUnGqQ3Dp6Isb95LMPxjjf3TT_KwAfWwUKzIm7P8kYer5xxUAIoHK6CrfNfpES7g8OxKLoV2dP_yEq9pQaLfo7Cw-Hciku4aWslv4At-O55JeQjz"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-light/50 via-background-light/80 to-background-light dark:from-background-dark/50 dark:via-background-dark/80 dark:to-background-dark"></div>
      </div>

      {/* Reservation Container */}
      <div className="relative z-10 w-full max-w-4xl grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        {/* Left Side: Content */}
        <div className="lg:col-span-5 space-y-6 text-center lg:text-left">
          <div>
            <span className="text-primary font-bold uppercase tracking-widest text-sm">
              Experience Afro-Caribbean
            </span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold mt-2 leading-tight text-secondary">
              Reserve <br /> <span className="text-primary">Your Seat</span>
            </h1>
          </div>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            Join us for an unforgettable dining experience. We can't wait to
            welcome you!
          </p>

          <div className="flex flex-col gap-4 mt-8">
            <div className="flex items-center gap-4 text-slate-700 dark:text-slate-300">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                <span className="material-icons text-xl">location_on</span>
              </div>
              <span className="text-left">
                112 Breck Road, Anfield, Liverpool L4 2RD
              </span>
            </div>
            <div className="flex items-center gap-4 text-slate-700 dark:text-slate-300">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                <span className="material-icons text-xl">phone</span>
              </div>
              <span className="text-left">07465 682308</span>
            </div>
          </div>
        </div>

        {/* Right Side: Reservation Form */}
        <div className="lg:col-span-7">
          <div className="bg-white/80 dark:bg-white/5 backdrop-blur-xl border border-white/20 dark:border-white/10 p-8 md:p-10 rounded-xl shadow-2xl shadow-primary/5">
            <h2 className="text-2xl font-bold mb-8 text-primary">
              Booking Details
            </h2>

            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* First & Last Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4 focus:border-primary focus:ring-0 transition-all"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4 focus:border-primary focus:ring-0 transition-all"
                    required
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4 focus:border-primary focus:ring-0 transition-all"
                  required
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4 focus:border-primary focus:ring-0 transition-all"
                  required
                />
              </div>

              {/* Date, Time, Seats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
                    Date
                  </label>
                  <input
                    type="date"
                    value={date}
                    min={minDate}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4 focus:border-primary focus:ring-0 transition-all"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
                    Time
                  </label>
                  <input
                    type="time"
                    min="12:00"
                    max="23:00"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4 focus:border-primary focus:ring-0 transition-all"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
                    Seats
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={seats}
                    onChange={(e) => setSeats(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4 focus:border-primary focus:ring-0 transition-all"
                    required
                  />
                </div>
              </div>

              {/* Special Requests */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
                  Special Requests
                </label>
                <textarea
                  rows={3}
                  value={specialRequest}
                  onChange={(e) => setSpecialRequest(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4 resize-none focus:border-primary focus:ring-0 transition-all"
                ></textarea>
              </div>

              {status.message && (
                <div
                  className={`mb-6 rounded-lg p-4 text-sm font-semibold ${
                    status.type === "success"
                      ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200"
                      : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200"
                  }`}
                >
                  {status.message}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`group relative w-full bg-primary text-white py-5 rounded-lg font-bold text-lg overflow-hidden shadow-xl shadow-primary/30 transition-all active:scale-95 ${
                  isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  {isSubmitting ? "Submitting..." : "Confirm Reservation"}
                  <span className="material-icons group-hover:translate-x-1 transition-transform">
                    arrow_forward
                  </span>
                </span>
                <div className="absolute inset-0 bg-white/10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              </button>
            </form>
          </div>
        </div>
      </div>
    </main>
  );
}
